import React, { createContext, useState, useContext, useEffect } from 'react';
import { authService, userService } from '../services/api';

interface User {
  id: number;
  username: string;
  email: string;
  fullName: string;
  role: string;
  isActive: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  permissions: string[];
  /**
   * True until the initial localStorage check (in the mount effect below) has run. Route guards
   * MUST wait for this to go false before deciding to redirect to /login — otherwise a hard page
   * reload renders once with the initial `token === null` state and immediately bounces an
   * already-logged-in user to /login before their token has been restored from storage, which
   * looks exactly like an unwanted logout. See App.tsx's PrivateRoute.
   */
  isInitializing: boolean;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isManager: () => boolean;
  hasRole: (role: string) => boolean;
  /** Returns true if the user has the given permission name, or is an Admin (wildcard). */
  hasPermission: (name: string) => boolean;
  /**
   * Returns true when:
   *  - The user has NO custom-role permissions assigned (backward-compatible: show everything), OR
   *  - The user explicitly has the given permission.
   * Use this for sidebar visibility so existing users without configured roles still see all items.
   */
  can: (name: string) => boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function decodeJwtPayload(token: string): Record<string, any> | null {
  try {
    const payloadBase64 = token.split('.')[1];
    const json = atob(payloadBase64.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json);
  } catch {
    return null;
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token);
  if (!payload?.exp) return true;
  return Date.now() / 1000 > payload.exp;
}

function getRolesFromToken(token: string): string[] {
  const payload = decodeJwtPayload(token);
  return payload?.realm_access?.roles ?? [];
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    const storedPerms = localStorage.getItem('permissions');

    if (storedToken && storedUser) {
      if (isTokenExpired(storedToken)) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('permissions');
      } else {
        setToken(storedToken);
        setUser(JSON.parse(storedUser));
        setPermissions(storedPerms ? JSON.parse(storedPerms) : []);
      }
    }
    setIsInitializing(false);
  }, []);

  const login = async (username: string, password: string) => {
    const response = await authService.login(username, password);
    setToken(response.token);
    setUser(response.user);
    localStorage.setItem('token', response.token);
    localStorage.setItem('user', JSON.stringify(response.user));

    // Fetch permissions after login — token is already in localStorage so the
    // interceptor will attach it to this request automatically.
    try {
      const perms = await userService.getMyPermissions();
      setPermissions(perms);
      localStorage.setItem('permissions', JSON.stringify(perms));
    } catch {
      // Non-fatal: user can still log in; they'll just see all items (backward-compat).
      setPermissions([]);
      localStorage.setItem('permissions', JSON.stringify([]));
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setPermissions([]);
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('permissions');
  };

  const hasRole = (role: string): boolean => {
    if (!token) return false;
    const tokenRoles = getRolesFromToken(token);
    if (tokenRoles.length > 0) return tokenRoles.includes(role);
    return user?.role === role;
  };

  const isAdmin = () => hasRole('Admin');
  const isManager = () => hasRole('Manager');

  const hasPermission = (name: string): boolean => {
    if (permissions.includes('*')) return true; // Admin wildcard
    return permissions.includes(name);
  };

  // Backward-compatible: if no custom role is configured, show everything.
  // Once a role with permissions is assigned, only granted items are shown.
  const can = (name: string): boolean => {
    if (permissions.includes('*')) return true;
    if (permissions.length === 0) return true;
    return permissions.includes(name);
  };

  return (
    <AuthContext.Provider value={{ user, token, permissions, isInitializing, login, logout, isAdmin, isManager, hasRole, hasPermission, can }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
